import { useCallback, useEffect, type RefObject } from 'react';
import type { ViewState } from '../shared/types';

interface UseViewTransformProps {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  canvasSize: { width: number; height: number };
  canvasRef: RefObject<HTMLCanvasElement>;
}

interface UseViewTransformReturn {
  worldToScreen: (worldX: number, worldY: number) => { x: number; y: number };
  screenToWorld: (screenX: number, screenY: number) => { x: number; y: number };
  zoomIn: () => void;
  zoomOut: () => void;
}

export const useViewTransform = ({
  viewState,
  setViewState,
  canvasSize,
  canvasRef
}: UseViewTransformProps): UseViewTransformReturn => {
  // Transform world coordinates to screen coordinates
  const worldToScreen = useCallback((worldX: number, worldY: number) => {
    const { offsetX, offsetY, scale, rotation } = viewState;
    const { width, height } = canvasSize;

    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    const pivotX = (width / 2) / scale;
    const pivotY = (height / 2) / scale;

    const translatedX = worldX + offsetX;
    const translatedY = worldY + offsetY;

    const pointRelativeToPivotX = translatedX - pivotX;
    const pointRelativeToPivotY = translatedY - pivotY;

    const rotatedX = pointRelativeToPivotX * cos - pointRelativeToPivotY * sin;
    const rotatedY = pointRelativeToPivotX * sin + pointRelativeToPivotY * cos;

    const finalX = rotatedX + pivotX;
    const finalY = rotatedY + pivotY;

    return {
      x: finalX * scale,
      y: finalY * scale
    };
  }, [viewState, canvasSize]);

  // Transform screen coordinates to world coordinates
  const screenToWorld = useCallback((screenX: number, screenY: number): { x: number; y: number } => {
    const { offsetX, offsetY, scale, rotation } = viewState;
    const { width, height } = canvasSize;

    const pivotX = (width / 2) / scale;
    const pivotY = (height / 2) / scale;

    // Unscale
    const scaledX = screenX / scale;
    const scaledY = screenY / scale;

    // Rotate back (inverse rotation)
    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);

    const pointRelativeToPivotX = scaledX - pivotX;
    const pointRelativeToPivotY = scaledY - pivotY;

    const rotatedX = pointRelativeToPivotX * cos - pointRelativeToPivotY * sin;
    const rotatedY = pointRelativeToPivotX * sin + pointRelativeToPivotY * cos;

    const worldX = rotatedX + pivotX - offsetX;
    const worldY = rotatedY + pivotY - offsetY;

    return { x: worldX, y: worldY };
  }, [viewState, canvasSize]);

  // Zoom in (towards center)
  const zoomIn = useCallback(() => {
    if (!canvasSize.width || !canvasSize.height) return;

    const centerX = canvasSize.width / 2;
    const centerY = canvasSize.height / 2;

    setViewState(prev => {
      const newScale = Math.min(10, prev.scale * 1.2);

      // Zoom relative to center - convert center from screen space to world space
      const worldCenterX = centerX / prev.scale - prev.offsetX;
      const worldCenterY = centerY / prev.scale - prev.offsetY;

      // Calculate new offset to keep center at the same world position
      const newOffsetX = centerX / newScale - worldCenterX;
      const newOffsetY = centerY / newScale - worldCenterY;

      return {
        ...prev,
        scale: newScale,
        offsetX: newOffsetX,
        offsetY: newOffsetY
      };
    });
  }, [canvasSize, setViewState]);

  // Zoom out (away from center)
  const zoomOut = useCallback(() => {
    if (!canvasSize.width || !canvasSize.height) return;

    const centerX = canvasSize.width / 2;
    const centerY = canvasSize.height / 2;

    setViewState(prev => {
      const newScale = Math.max(0.1, prev.scale / 1.2);

      // Zoom relative to center - convert center from screen space to world space
      const worldCenterX = centerX / prev.scale - prev.offsetX;
      const worldCenterY = centerY / prev.scale - prev.offsetY;

      // Calculate new offset to keep center at the same world position
      const newOffsetX = centerX / newScale - worldCenterX;
      const newOffsetY = centerY / newScale - worldCenterY;

      return {
        ...prev,
        scale: newScale,
        offsetX: newOffsetX,
        offsetY: newOffsetY
      };
    });
  }, [canvasSize, setViewState]);

  // Native wheel handler for zoom (to properly preventDefault)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const zoomIntensity = 0.1;
      const delta = e.deltaY > 0 ? -1 : 1;
      const scaleFactor = 1 + delta * zoomIntensity;

      setViewState(prev => {
        const newScale = Math.max(0.1, Math.min(10, prev.scale * scaleFactor));

        // Zoom relative to mouse position - convert mouse from screen space to world space
        const worldMouseX = mouseX / prev.scale - prev.offsetX;
        const worldMouseY = mouseY / prev.scale - prev.offsetY;

        // Calculate new offset to keep mouse at the same world position
        const newOffsetX = mouseX / newScale - worldMouseX;
        const newOffsetY = mouseY / newScale - worldMouseY;

        return {
          scale: newScale,
          offsetX: newOffsetX,
          offsetY: newOffsetY,
          rotation: prev.rotation
        };
      });
    };

    // Add listener with passive: false to allow preventDefault
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [canvasRef, setViewState]);

  return {
    worldToScreen,
    screenToWorld,
    zoomIn,
    zoomOut
  };
};
